import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { useContext } from "react";
import axios from "axios";
import config from "../../config";
import { AppContext } from "../../AppContext";
import { useMasterPassword } from "../../components/MasterPasswordDialog/MasterPasswordContext";
import {
    buildDownloadPdfUrl,
    getProfileLetterhead,
} from "../../utils/printFormatUrl";

const fetchOrderInvoice = (orderId) => {
    const params = {
        form_name: `payment-order`,
        doc_name: orderId,
    };
    return axios.post(
        `method/frappe_helper.frappe_helper.doctype.desk_form.desk_form.get_form_data`,
        params
    );
};

export const useOrderInvoiceData = (orderId, onSuccess, onError) => {
    return useQuery(
        ["OrderInvoiceData", orderId],
        async () => {
            const { data } = await fetchOrderInvoice(orderId);
            return data.message;
        },
        {
            enabled: !!orderId,
        }
    );
};

const createOrderInvoice = (orderId, formData, masterPassword) => {
    const params = {
        model: `Table Order`,
        name: orderId,
        method: `make_invoice`,
        args: JSON.stringify(formData),
    };
    
    if (masterPassword) {
        params.master_password = masterPassword;
    }
    
    try {
        return axios.post(
            masterPassword
                ? `method/restaurant_management.api.escalate_privilege`
                : `method/frappe_helper.api.call`,
            params
        );
    } catch (err) {
        throw new err();
    }
};

export const useCrateOrderInvoice = () => {
    const { showMasterPasswordDialog } = useMasterPassword();
    const queryClient = useQueryClient();
    
    const mutation = useMutation(
        async ({ orderId, formData, masterPassword }) => {
            const data = await createOrderInvoice(orderId, formData, masterPassword);
            return data;
        },
        {
            onSettled: (data, error, variables, context) => {
                const { orderId } = variables;
                queryClient.invalidateQueries(["getTableOrdersData"]);
                queryClient.invalidateQueries(["getTableOrderItems", orderId]);
            },
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                console.log("Master password dialog cancelled");
                options.onCancel?.(error, otherParams);
                return;
            }
        } else {
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};

const fetchInvoicePrintData = (invoiceId, posProfile) => {
    let token = localStorage.getItem("accessToken");
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);

    const selectedLetterhead = getProfileLetterhead(posProfile);

    const requestOptions = {
        method: "GET",
        headers: myHeaders,
    };

    const backUrl = config.baseUrl;
    const url = buildDownloadPdfUrl({
        baseUrl: backUrl,
        doctype: "POS Invoice",
        name: invoiceId,
        format: "POS",
        noLetterhead: selectedLetterhead ? 0 : 1,
        letterhead: selectedLetterhead || "No Letterhead",
        extraParams: {
            model: "POS Invoice",
        },
    });

    try {
        return fetch(url, requestOptions);
    } catch (err) {
        throw err;
    }
};

export const useOrderInvoicePrintData = (invoiceId, onSuccess, onError) => {
    const queryClient = useQueryClient();
    const { posProfile } = useContext(AppContext);

    return useQuery(
        ["OrderInvoicePrintData", invoiceId, posProfile?.letter_head],
        async () => {
            const response = await fetchInvoicePrintData(invoiceId, posProfile);
            const blob = await response.blob();
            return URL.createObjectURL(blob);
        },
        {
            onSuccess: (data) => {
                queryClient.refetchQueries(["OrderInvoiceData", invoiceId]);

                if (onSuccess) onSuccess();
            },
            refetchOnmount: true,
            onError,
            enabled: !!invoiceId,
        }
    );
};


const formatFrappeDateTime = (date) => {
    const pad = (value) => String(value).padStart(2, "0");
    return [
        date.getFullYear(),
        pad(date.getMonth() + 1),
        pad(date.getDate()),
    ].join("-") + " " + [
        pad(date.getHours()),
        pad(date.getMinutes()),
        pad(date.getSeconds()),
    ].join(":");
};

const getBusinessDayStart = () => {
    const date = new Date();
    if (date.getHours() < 4) {
        date.setDate(date.getDate() - 1);
    }
    date.setHours(4, 0, 0, 0);
    return formatFrappeDateTime(date);
};

const createClosedInvoiceQuery = ({
    mode = "today",
    search = "",
    status,
    posProfile,
}) => {
    const filters = [];
    // Only fetch rows that can render in ClosedOrders. Client-side filtering
    // after pagination can produce empty visible pages when returns/non-paid
    // invoices occupy the first backend page.
    // Cancelled POS Invoices have docstatus 2; the status filter below keeps
    // this list restricted to the closed states we explicitly support.
    filters.push(["docstatus", "in", [1, 2]]);
    if (status) {
        filters.push(["status", "=", status]);
    } else {
        filters.push([
            "status",
            "in",
            ["Paid", "Cancelled", "Consolidated", "Submitted"],
        ]);
    }
    filters.push(["return_against", "is", "not set"]);
    if (posProfile) {
        filters.push(["pos_profile", "=", posProfile]);
    }
    if (mode === "today") {
        filters.push(["creation", ">=", getBusinessDayStart()]);
    }

    const q = String(search || "").trim();
    const orFilters = [];
    if (q) {
        // Server-side search across common invoice fields
        const like = `%${q}%`;
        orFilters.push(["name", "like", like]);
        orFilters.push(["customer", "like", like]);
        orFilters.push(["customer_name", "like", like]);
    }

    return { filters, orFilters };
};

const fetchTodayClosedInvoices = () => {
    const params = {
        fields: JSON.stringify(["*"]),
        filters: JSON.stringify([["creation", ">=", getBusinessDayStart()]]),
        limit: 0, // Set the limit to 0 to load all items
    };

    return axios.get(`/resource/POS Invoice`, { params });
};

export const useTodayClosedInvoices = (onSuccess, onError) => {
    return useQuery(
        ["getTodayClosedInvoices"],
        async () => {
            const data = await fetchTodayClosedInvoices();
            return data.data.data;
        },
        {
            onSuccess,
            onError,
            cacheTime: 30 * 1000 * 60 * 60 * 24,
        }
    );
};

const fetchClosedInvoicesPage = async ({
    mode = "today",
    limit = 50,
    offset = 0,
    search = "",
    status,
    posProfile,
}) => {
    const { filters, orFilters } = createClosedInvoiceQuery({
        mode,
        search,
        status,
        posProfile,
    });

    const params = {
        fields: JSON.stringify(["*"]),
        filters: JSON.stringify(filters),
        limit,
        limit_page_length: limit,
        limit_start: offset,
        order_by: "creation desc",
    };
    if (orFilters.length) {
        params.or_filters = JSON.stringify(orFilters);
    }

    const res = await axios.get(`/resource/POS Invoice`, { params });
    return res.data.data || [];
};

const fetchClosedInvoicesCount = async ({
    mode = "today",
    search = "",
    status,
    posProfile,
}) => {
    const { filters } = createClosedInvoiceQuery({
        mode,
        search,
        status,
        posProfile,
    });
    const payload = {
        doctype: "POS Invoice",
        filters: JSON.stringify(filters),
        fields: JSON.stringify([]),
        distinct: false,
    };

    const res = await axios.post(
        "method/frappe.desk.reportview.get_count",
        payload
    );
    return Number(res.data?.message || 0);
};

/**
 * Paginated list of POS Invoices with Today/All mode and optional server-side search.
 * Uses infinite pagination; consumers call fetchNextPage() to load more.
 */
export const useClosedInvoices = ({
    mode = "today",
    limit = 50,
    search = "",
    status,
    posProfile,
    enabled = true,
} = {}) => {
    const pageLimit = mode === "today" ? 0 : limit;

    return useInfiniteQuery({
        queryKey: ["closedInvoices", mode, pageLimit, search, status, posProfile],
        queryFn: async ({ pageParam = 0 }) => {
            const [rows, totalCount] = await Promise.all([
                fetchClosedInvoicesPage({
                    mode,
                    limit: pageLimit,
                    offset: pageParam,
                    search,
                    status,
                    posProfile,
                }),
                fetchClosedInvoicesCount({
                    mode,
                    search,
                    status,
                    posProfile,
                }),
            ]);
            rows.totalCount = totalCount;
            return rows;
        },
        getNextPageParam: (lastPage, allPages) => {
            if (!pageLimit) return undefined;
            if (!Array.isArray(lastPage)) return undefined;
            if (lastPage.length === 0) return undefined;
            const totalCount = Number(allPages?.[0]?.totalCount || 0);
            const loadedCount = allPages.reduce(
                (offset, page) => offset + (Array.isArray(page) ? page.length : 0),
                0
            );
            return loadedCount < totalCount ? loadedCount : undefined;
        },
        enabled: !!enabled,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        cacheTime: 5 * 60 * 1000,
    });
};

export const useCancelInvoice = () => {
    return useMutation(async ({ invoice, duplicate_order = false }) => {
        const payload = {
            invoice,
            duplicate_order: !!duplicate_order,
        };
        const { data } = await axios.post(
            "method/restaurant_management.api.cancel_invoice",
            payload
        );
        return data?.message ?? data;
    });
};
